import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Heart, Sparkles, Copy, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

interface ToolPageProps {
  title: string;
  metaDescription: string;
  h1: string;
  subtitle: string;
  toolLabel: string;
  placeholder: string;
  systemPrompt: string;
  seoContent: React.ReactNode;
  faq: { q: string; a: string }[];
  relatedPages: { label: string; href: string }[];
  relationships?: string[];
}

export function MessageToolPage({
  title, metaDescription, h1, subtitle, toolLabel, placeholder, systemPrompt,
  seoContent, faq, relatedPages, relationships = ['Parent', 'Partner', 'Friend', 'Sibling', 'Mentor', 'Other'],
}: ToolPageProps) {
  useDocumentHead({ title, description: metaDescription });

  const [recipientName, setRecipientName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [context, setContext] = useState('');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!recipientName.trim()) { toast.error('Please enter a name'); return; }
    setLoading(true);
    setResult('');
    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/announcement-ai-assist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}` },
        body: JSON.stringify({
          action: 'help_write',
          recipientName: recipientName.trim(),
          relationship: relationship || 'someone special',
          currentMessage: context.trim() || 'I want to express my feelings.',
          context: systemPrompt,
        }),
      });
      const data = await res.json();
      setResult(data.suggestion || data.message || 'Something went wrong. Please try again.');
    } catch {
      toast.error('Failed to generate message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyResult = () => {
    navigator.clipboard.writeText(result);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        <section className="container max-w-3xl py-12 md:py-20">
          <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground leading-tight mb-4">{h1}</h1>
          <p className="text-lg text-muted-foreground leading-relaxed">{subtitle}</p>
        </section>

        {/* Tool */}
        <section className="container max-w-2xl pb-12">
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-lg space-y-5">
            <h2 className="text-xl font-bold font-heading text-foreground flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" /> {toolLabel}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Recipient's Name</label>
                <Input value={recipientName} onChange={e => setRecipientName(e.target.value)} placeholder="e.g. Mom, Sarah, Dad" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Relationship</label>
                <Select value={relationship} onValueChange={setRelationship}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {relationships.map(r => <SelectItem key={r} value={r.toLowerCase()}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Context (optional)</label>
              <Textarea value={context} onChange={e => setContext(e.target.value)} placeholder={placeholder} rows={3} />
            </div>
            <Button onClick={generate} disabled={loading} className="w-full gap-2">
              {loading ? 'Generating...' : <><Heart className="w-4 h-4" /> Generate Message</>}
            </Button>

            {result && (
              <div className="mt-4 space-y-3">
                <div className="rounded-lg bg-primary/5 dark:bg-primary/10 p-4 text-foreground whitespace-pre-wrap leading-relaxed">{result}</div>
                <div className="flex gap-3">
                  <Button variant="outline" size="sm" onClick={copyResult} className="gap-1"><Copy className="w-3 h-3" /> Copy</Button>
                  <Button asChild size="sm" className="gap-1"><Link to={`/signup?prefill=${encodeURIComponent(result.slice(0, 500))}`}>Create Full Message <ArrowRight className="w-3 h-3" /></Link></Button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* SEO content */}
        <section className="container max-w-3xl pb-12 prose prose-lg dark:prose-invert prose-headings:font-heading prose-a:text-primary">
          {seoContent}
        </section>

        {/* CTA */}
        <section className="bg-primary/5 dark:bg-primary/10 py-12">
          <div className="container max-w-3xl text-center">
            <h2 className="text-2xl font-bold font-heading text-foreground mb-4">Want something more beautiful?</h2>
            <p className="text-muted-foreground mb-6">Turn your message into a stunning Living Announcement with photos, animations, and a permanent keepsake link.</p>
            <Button asChild size="lg" className="gap-2"><Link to="/signup"><Heart className="w-4 h-4" /> Create Your Free Announcement</Link></Button>
          </div>
        </section>

        {/* FAQ */}
        {faq.length > 0 && (
          <section className="container max-w-3xl py-12">
            <h2 className="text-2xl font-bold font-heading text-foreground mb-6">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-foreground">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        {/* Related */}
        {relatedPages.length > 0 && (
          <section className="container max-w-3xl pb-12">
            <h3 className="text-lg font-semibold text-foreground mb-4">Explore More</h3>
            <div className="flex flex-wrap gap-3">
              {relatedPages.map(p => (
                <Link key={p.href} to={p.href} className="text-sm text-primary hover:text-primary/80 underline underline-offset-2">{p.label}</Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
